import { JobStatus, Patient, IPatient } from '.';
import { Itinerary, VaccinationDetails } from '../types';
import { isSame, isAfter, isBefore, getDifference } from '../utils';

export enum VaccinationCategory {
    COVID_19 = 'COVID-19',
    FLU = 'FLU',
    SHINGLES = 'SHINGLES',
    PNEUMOCOCCAL = 'PNEUMOCOCCAL',
}

export enum VaccinationType {
    PRIMARY = 'PRIMARY',
    BOOSTER = 'BOOSTER',
}

export enum Covid19Vaccine {
    PFIZER = 'PFIZER',
    ASTRAZENECA = 'ASTRAZENECA',
    MODERNA = 'MODERNA',
    COMIRNATY = 'COMIRNATY',
    JOHNSON_JOHNSON = 'JOHNSON_JOHNSON',
    SPIKEVAX_BIVALENT = 'SPIKEVAX_BIVALENT',
    PFIZER_BIVALENT = 'PFIZER_BIVALENT',
    NOVAVAX = 'NOVAVAX',
    GSK_MONOVALENT = 'GSK_MONOVALENT',
    COMIRNATY_30_XBB_1_5 = 'COMIRNATY_30_XBB_1_5',
    SPIKEVAX_XBB_1_5 = 'SPIKEVAX_XBB_1_5',
}

export enum FluVaccine {
    QUADRIVALENT_MASTA = 'QUADRIVALENT_MASTA',
    QUADRIVALENT_SANOFI = 'QUADRIVALENT_SANOFI',
    QUADRIVALENT_INFLUVAC = 'QUADRIVALENT_INFLUVAC',
    FLUCELVAX_TETRA = 'FLUCELVAX_TETRA',
    FLUAD_TETRA = 'FLUAD_TETRA',
    FLUENZ_TETRA = 'FLUENZ_TETRA',
    SUPEMTEK = 'SUPEMTEK',
    SEQIRUS = 'SEQIRUS',
    ADJUVANTED_SEQIRUS = 'ADJUVANTED_SEQIRUS',
}

export enum ShinglesVaccine {
    ZOSTAVAX = 'ZOSTAVAX',
    SHINGRIX = 'SHINGRIX',
}

export enum PneumococcalVaccine {
    PNEUMOVAX_23 = 'PNEUMOVAX_23',
}

export enum ConsentStatus {
    CONSENTED = 'CONSENTED',
    UNKNOWN = 'UNKNOWN',
    DID_NOT_CONSENT = 'DID_NOT_CONSENT',
}

export const friendlyVaccinationCategories = {
    'COVID-19': 'COVID-19',
    FLU: 'Flu',
    SHINGLES: 'Shingles',
    PNEUMOCOCCAL: 'Pneumococcal',
};

export const friendlyCovid19Manufacturers = {
    PFIZER: 'Pfizer',
    ASTRAZENECA: 'AstraZeneca',
    MODERNA: 'Moderna',
    COMIRNATY: 'Comirnaty',
    JOHNSON_JOHNSON: 'Johnson & Johnson',
    SPIKEVAX_BIVALENT: 'Spikevax Bivalent',
    PFIZER_BIVALENT: 'Comirnaty Bivalent',
    NOVAVAX: 'Novavax',
    GSK_MONOVALENT: 'Sanofi/GSK monovalent',
    COMIRNATY_30_XBB_1_5: 'Comirnaty 30 XBB 1.5',
    SPIKEVAX_XBB_1_5: 'Spikevax XBB 1.5',
};

export const friendlyFluManufacturers = {
    QUADRIVALENT_MASTA: 'Quadrivalent (MASTA)',
    QUADRIVALENT_SANOFI: 'Quadrivalent (Sanofi)',
    QUADRIVALENT_INFLUVAC: 'Quadrivalent Influvac sub-unit Tetra',
    FLUCELVAX_TETRA: 'Flucelvax Tetra',
    FLUAD_TETRA: 'Fluad Tetra',
    FLUENZ_TETRA: 'Fluenz Tetra',
    SUPEMTEK: 'Supemtek',
    SEQIRUS: 'Quadrivalent Seqirus 2yrs+',
    ADJUVANTED_SEQIRUS: 'Adjuvanted Quadrivalent Seqirus 65yrs+',
};

export const friendlyShinglesManufacturers = {
    ZOSTAVAX: 'Zostavax',
    SHINGRIX: 'Shingrix',
};

export const friendlyPneumococcalManufacturers = {
    PNEUMOVAX_23: 'Pneumovax 23',
};

export const friendlyConsentStatuses = {
    CONSENTED: 'Consented',
    UNKNOWN: 'Unknown',
    DID_NOT_CONSENT: 'Did not consent',
};

const initialValues = {
    consented: '',
    doseNumber: '',
    hubId: '',
    dateOfDose1: '',
    dateOfDose2: '',
    dateOfDose3: '',
    dateOfDose3Primary: '',
    dateOfDose4: '',
    dateOfPreviousDose: '',
    manufacturerOfDose1: '',
    manufacturerOfDose2: '',
    manufacturerOfDose3: '',
    manufacturerOfDose3Primary: '',
    manufacturerOfDose4: '',
    manufacturerOfPreviousDose: '',
    vaccinationCategory: '',
    coAdministeredWith: [],
};

export type VaccineManufacturer =
    | Covid19Vaccine
    | FluVaccine
    | ShinglesVaccine
    | PneumococcalVaccine;

export const friendlyManufacturers = {
    ...friendlyCovid19Manufacturers,
    ...friendlyFluManufacturers,
    ...friendlyShinglesManufacturers,
    ...friendlyPneumococcalManufacturers,
};

export interface IVaccination extends IPatient {
    consented: ConsentStatus;
    doseNumber: number | null;
    hubId: string;
    vaccinationCategory: VaccinationCategory;
    vaccinationType: VaccinationType;
    coAdministeredWith?: VaccinationCategory[];
    dateOfDose1?: string;
    dateOfDose2?: string;
    dateOfDose3?: string;
    dateOfDose3Primary?: string;
    dateOfDose4?: string;
    dateOfPreviousDose?: string;
    itineraryId?: string;
    itinerary?: Itinerary | null;
    manufacturerOfDose1?: VaccineManufacturer;
    manufacturerOfDose2?: VaccineManufacturer;
    manufacturerOfDose3?: VaccineManufacturer;
    manufacturerOfDose3Primary?: VaccineManufacturer;
    manufacturerOfDose4?: VaccineManufacturer;
    manufacturerOfPreviousDose?: VaccineManufacturer;
    vaccineBatchNumber?: string;
    vaccineExpiryDate?: string;
    vaccineManufacturer?: VaccineManufacturer;
    vaccineSiteOfAdministration?: string;
    routeType?: string;
    routeTraceId?: string;
    requestId?: string;
    priceInPence?: number;
}

interface Vaccination extends IVaccination {}

class Vaccination extends Patient {
    static INITIAL_VALUES = {
        ...Patient.INITIAL_VALUES,
        ...initialValues,
    };

    static IMMUTABLE_PROPERTIES = [...Patient.IMMUTABLE_PROPERTIES, 'vaccinationCategory'];

    constructor(data: IVaccination) {
        super(data);
        Object.assign(this, data);
    }

    static getFriendlyConsentStatus(consented: ConsentStatus): string {
        return friendlyConsentStatuses[consented];
    }

    get friendlyConsentStatus(): string | null {
        return this.consented ? friendlyConsentStatuses[this.consented] : null;
    }

    static getFriendlyVaccinationCategory(vaccinationCategory: VaccinationCategory): string {
        return friendlyVaccinationCategories[vaccinationCategory];
    }

    get friendlyVaccinationCategory(): string | null {
        return this.vaccinationCategory
            ? friendlyVaccinationCategories[this.vaccinationCategory]
            : null;
    }

    get friendlyCoAdministeredWith(): string[] {
        return (this.coAdministeredWith || []).map(Vaccination.getFriendlyVaccinationCategory);
    }

    static getFriendlyManufacturer(vaccineManufacturer: VaccineManufacturer): string {
        return friendlyManufacturers[vaccineManufacturer];
    }

    static manufacturerList(
        vaccinationCategory: VaccinationCategory,
    ): Record<string, string> | null {
        switch (vaccinationCategory) {
            case VaccinationCategory.COVID_19:
                return friendlyCovid19Manufacturers;
            case VaccinationCategory.FLU:
                return friendlyFluManufacturers;
            case VaccinationCategory.SHINGLES:
                return friendlyShinglesManufacturers;
            case VaccinationCategory.PNEUMOCOCCAL:
                return friendlyPneumococcalManufacturers;
            default:
                return null;
        }
    }

    get getDateOfPreviousDose(): string | undefined {
        if (this.doseNumber === 5) {
            return this.dateOfDose4;
        }
        if (this.doseNumber === 4) {
            return this.dateOfDose3;
        }
        if (this.doseNumber === 3) {
            return this.dateOfDose3Primary || this.dateOfDose2;
        }
        return this.dateOfDose1;
    }

    get getManufacturerOfPreviousDose(): VaccineManufacturer | undefined {
        if (this.doseNumber === 5) {
            return this.manufacturerOfDose4;
        }
        if (this.doseNumber === 4) {
            return this.manufacturerOfDose3;
        }
        if (this.doseNumber === 3) {
            return this.manufacturerOfDose3Primary || this.manufacturerOfDose2;
        }
        return this.manufacturerOfDose1;
    }

    get friendlyManufacturerOfPreviousDose(): string | null {
        return this.manufacturerOfPreviousDose
            ? Vaccination.getFriendlyManufacturer(this.manufacturerOfPreviousDose)
            : null;
    }

    get friendlyManufacturerOfDose1(): string | null {
        return this.manufacturerOfDose1
            ? Vaccination.getFriendlyManufacturer(this.manufacturerOfDose1)
            : null;
    }

    get friendlyManufacturerOfDose2(): string | null {
        return this.manufacturerOfDose2
            ? Vaccination.getFriendlyManufacturer(this.manufacturerOfDose2)
            : null;
    }

    get friendlyManufacturerOfDose3(): string | null {
        return this.manufacturerOfDose3
            ? Vaccination.getFriendlyManufacturer(this.manufacturerOfDose3)
            : null;
    }

    get friendlyManufacturerOfDose4(): string | null {
        return this.manufacturerOfDose4
            ? Vaccination.getFriendlyManufacturer(this.manufacturerOfDose4)
            : null;
    }

    get friendlyManufacturer(): string | null {
        return this.vaccineManufacturer
            ? Vaccination.getFriendlyManufacturer(this.vaccineManufacturer)
            : null;
    }

    get selectable(): boolean {
        return (
            this.consented !== ConsentStatus.DID_NOT_CONSENT && this.jobStatus === JobStatus.PENDING
        );
    }

    get vaccinationOverdue(): boolean {
        if (!this.getDateOfPreviousDose) {
            return false;
        }

        const differenceInWeeks = getDifference(this.getDateOfPreviousDose, new Date(), 'week')
            .weeks;

        if (this.doseNumber === 2 && differenceInWeeks > 10) {
            return true;
        }
        if (this.doseNumber === 3 && differenceInWeeks > 30) {
            return true;
        }
        if (this.doseNumber === 4 && differenceInWeeks > 30) {
            return true;
        }
        if (this.doseNumber === 5 && differenceInWeeks > 30) {
            return true;
        }
        return false;
    }

    get requiresAttention(): string[] {
        const requiresAttention = [];
        if (this.vaccinationOverdue) {
            requiresAttention.push('dateOfPreviousDose');
        }
        return requiresAttention;
    }

    static convertDate(date: string | undefined): Date | null {
        const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
        if (date) {
            return dateRegex.test(date)
                ? new Date(
                      `${date.substring(3, 5)}/${date.substring(0, 2)}/${date.substring(6, 10)}`,
                  )
                : new Date(date);
        }
        return null;
    }

    setItinerary(itinerary: Itinerary): void {
        this.itinerary = itinerary;
    }

    static getVaccinationWarnings(
        job: IVaccination,
        selectedDate: string | null,
        doseInterval: Record<string, Record<string, number>>,
        vaccinationDetails?: VaccinationDetails,
    ): string[] {
        const warnings = [];
        const {
            dateOfDose1,
            dateOfDose2,
            dateOfPreviousDose,
            manufacturerOfDose1,
            manufacturerOfDose2,
            manufacturerOfPreviousDose,
            vaccinationType,
            vaccinationCategory,
            postCode,
            doseNumber,
            odsCode,
        } = job;

        if (!odsCode && vaccinationDetails?.[vaccinationCategory]?.requiresPickup) {
            warnings.push('No ODS Code has been provided');
        }

        const convertedDateOfDose1 = this.convertDate(dateOfDose1);
        const convertedDateOfDose2 = this.convertDate(dateOfDose2);
        const convertedDateOfPreviousDose = this.convertDate(dateOfPreviousDose);
        const convertedSelectedDate = selectedDate ? new Date(selectedDate) : null;
        const pushIncompleteWarning = (warning: string) =>
            warnings.push(`Incomplete vaccination history: ${warning}`);
        const pushInconsistentHistoryWarning = (warning: string) =>
            warnings.push(`Inconsistent vaccination history: ${warning}`);
        const pushInconsistentRequestWarning = (warning: string) =>
            warnings.push(`Inconsistent vaccination request: ${warning}`);
        const covidDoseInterval: Record<string, number> =
            doseInterval && doseInterval[VaccinationCategory.COVID_19]
                ? doseInterval[VaccinationCategory.COVID_19]
                : {};
        const primaryDoseInterval = covidDoseInterval.primaryToPrimary || 28;
        const primaryToBoosterDoseInterval = covidDoseInterval.primaryToBooster || 91;
        const boosterToBoosterDoseInterval = covidDoseInterval.boosterToBooster || 91;

        if (vaccinationCategory === VaccinationCategory.COVID_19) {
            if (convertedDateOfDose1 && !manufacturerOfDose1) {
                pushIncompleteWarning("field 'Covid-19 1st Dose - Manufacturer' missing");
            }
            if (!convertedDateOfDose1 && manufacturerOfDose1) {
                pushIncompleteWarning("field 'Covid-19 1st Dose - Date' missing");
            }
            if (convertedDateOfDose2 && !manufacturerOfDose2) {
                pushIncompleteWarning("field 'Covid-19 2nd Dose - Manufacturer' missing");
            }
            if (!convertedDateOfDose2 && manufacturerOfDose2) {
                pushIncompleteWarning("field 'Covid-19 2nd Dose - Date' missing");
            }
            if (
                (vaccinationType === VaccinationType.BOOSTER ||
                    convertedDateOfDose1 ||
                    convertedDateOfDose2 ||
                    manufacturerOfPreviousDose) &&
                !convertedDateOfPreviousDose
            ) {
                pushIncompleteWarning("field 'Previous covid vaccination date' missing");
            }
            if (
                (vaccinationType === VaccinationType.BOOSTER ||
                    convertedDateOfDose1 ||
                    convertedDateOfDose2 ||
                    convertedDateOfPreviousDose) &&
                !manufacturerOfPreviousDose
            ) {
                pushIncompleteWarning("field 'Previous covid vaccination manufacturer' missing");
            }
            if (
                (convertedDateOfDose2 || manufacturerOfDose2) &&
                (!convertedDateOfDose1 || !manufacturerOfDose1)
            ) {
                pushIncompleteWarning('information for 1st dose missing');
            }

            if (
                convertedDateOfDose1 &&
                convertedDateOfDose2 &&
                isAfter(convertedDateOfDose1, convertedDateOfDose2)
            ) {
                pushInconsistentHistoryWarning('sequencing of dose 1 and 2 is inconsistent');
            }
            if (
                convertedDateOfDose1 &&
                convertedDateOfDose2 &&
                getDifference(convertedDateOfDose1, convertedDateOfDose2).days < primaryDoseInterval
            ) {
                pushInconsistentHistoryWarning(
                    'dose 1 and 2 do not follow the recommended time gap for primary doses',
                );
            }
            if (
                (convertedDateOfPreviousDose || manufacturerOfPreviousDose) &&
                (!convertedDateOfDose1 || !manufacturerOfDose1)
            ) {
                pushIncompleteWarning('no history of primary doses');
            }
            if (convertedDateOfPreviousDose) {
                if (
                    (convertedDateOfDose1 &&
                        isBefore(convertedDateOfPreviousDose, convertedDateOfDose1)) ||
                    (convertedDateOfDose2 &&
                        isBefore(convertedDateOfPreviousDose, convertedDateOfDose2))
                ) {
                    pushInconsistentHistoryWarning(
                        'sequencing of previous dose is inconsistent with primary course',
                    );
                }
            }
            if (vaccinationType === VaccinationType.PRIMARY && convertedDateOfDose2) {
                pushInconsistentRequestWarning('primary course already completed');
            }
            if (
                vaccinationType === VaccinationType.PRIMARY &&
                convertedDateOfDose1 &&
                convertedSelectedDate &&
                getDifference(convertedDateOfDose1, convertedSelectedDate).days <
                    primaryDoseInterval
            ) {
                pushInconsistentRequestWarning(
                    'requested dose does not follow the recommended time gap for primary doses',
                );
            }
            if (
                vaccinationType === VaccinationType.BOOSTER &&
                (!convertedDateOfDose1 || !convertedDateOfDose2)
            ) {
                pushInconsistentRequestWarning('primary course incomplete');
            }
            if (
                vaccinationType === VaccinationType.BOOSTER &&
                convertedDateOfDose2 &&
                convertedDateOfPreviousDose &&
                isSame(convertedDateOfDose2, convertedDateOfPreviousDose, 'day') &&
                convertedSelectedDate &&
                getDifference(convertedDateOfDose2, convertedSelectedDate).days <
                    primaryToBoosterDoseInterval
            ) {
                pushInconsistentRequestWarning(
                    'requested dose does not follow the recommended time gap for booster doses',
                );
            }
            if (
                vaccinationType === VaccinationType.BOOSTER &&
                convertedDateOfDose2 &&
                convertedDateOfPreviousDose &&
                !isSame(convertedDateOfDose2, convertedDateOfPreviousDose, 'day') &&
                convertedSelectedDate &&
                getDifference(convertedDateOfDose2, convertedSelectedDate).days <
                    boosterToBoosterDoseInterval
            ) {
                pushInconsistentRequestWarning(
                    'requested dose does not follow the recommended time gap for booster doses',
                );
            }
            if (doseNumber && !convertedDateOfPreviousDose) {
                pushIncompleteWarning('field Previous vaccination date missing');
            }
            if (!vaccinationType) {
                pushInconsistentRequestWarning('field Vaccination type missing');
            }
        }
        if (postCode && !postCode.match(/^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i)) {
            warnings.push(`The postcode ${postCode} does not appear valid.`);
        }
        return warnings;
    }
}

export { Vaccination };
